import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { AlertOctagon, AlertTriangle, Info, Check, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";

const SEVERITY = {
  info: { icon: Info, cls: "text-sky-400 border-sky-500/40" },
  warn: { icon: AlertTriangle, cls: "text-amber-400 border-amber-500/50" },
  critical: { icon: AlertOctagon, cls: "text-red-400 border-red-500/50" },
};

const TYPE_LABEL = {
  "feed.auto_paused_errors": "Auto-paused (errors)",
  "feed.freshness_paused_15d": "Paused (stale >15d)",
  "feed.freshness_downgraded_daily": "Slowed to daily",
  "feed.interval_changed": "Interval changed",
  "feed.enabled_toggled": "Enabled toggled",
  "webhook.gave_up": "Webhook gave up",
  "system.paused": "System paused",
  "system.resumed": "System resumed",
};

export default function Notifications() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("unread"); // unread | all
  const [severity, setSeverity] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", filter, severity],
    queryFn: async () =>
      (
        await api.get("/notifications", {
          params: {
            unread_only: filter === "unread" ? true : undefined,
            severity: severity || undefined,
            limit: 200,
          },
        })
      ).data,
    refetchInterval: 30000,
  });

  const markRead = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => (await api.post("/notifications/read-all")).data,
    onSuccess: (r) => {
      toast.success(`${r.updated} notifications marked as read`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  const delOne = useMutation({
    mutationFn: async (id) => (await api.delete(`/notifications/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  const clearRead = useMutation({
    mutationFn: async () => (await api.delete("/notifications?read_only=true")).data,
    onSuccess: (r) => {
      toast.success(`${r.deleted} read notifications deleted`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const items = data?.items || [];
  const unread = data?.unread ?? 0;
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Platform log</div>
        <h1 className="text-4xl sm:text-5xl font-mono font-semibold tracking-tight">Notifications</h1>
        <div className="mt-2 text-xs font-mono text-zinc-500" data-testid="notifications-summary">
          {total} shown · {unread} unread
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex border border-zinc-800 rounded-sm">
          <button
            data-testid="filter-unread"
            onClick={() => setFilter("unread")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${filter === "unread" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-white"}`}
          >
            Unread
          </button>
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-zinc-800 ${filter === "all" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-white"}`}
          >
            All
          </button>
        </div>
        <select
          data-testid="severity-filter"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="bg-transparent border border-zinc-800 rounded-sm font-mono text-xs px-2 py-1.5 text-zinc-200 uppercase tracking-widest"
        >
          <option value="">All severities</option>
          <option value="info">Info</option>
          <option value="warn">Warning</option>
          <option value="critical">Critical</option>
        </select>
        <div className="flex-1" />
        <Button
          data-testid="mark-all-read-btn"
          variant="outline"
          className="border-zinc-800 bg-transparent hover:bg-zinc-900 rounded-sm gap-2"
          disabled={unread === 0}
          onClick={() => markAllRead.mutate()}
        >
          <CheckCheck className="h-4 w-4" /> Mark all read
        </Button>
        <Button
          data-testid="clear-read-btn"
          variant="outline"
          className="border-zinc-800 bg-transparent hover:bg-zinc-900 rounded-sm gap-2 text-zinc-400"
          onClick={() => clearRead.mutate()}
        >
          <Trash2 className="h-4 w-4" /> Clear read
        </Button>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="notifications-list">
        {isLoading && <div className="text-zinc-500 font-mono text-xs">LOADING…</div>}
        {!isLoading && items.length === 0 && (
          <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500 font-mono text-xs">
            — {filter === "unread" ? "no unread notifications, you're all caught up" : "nothing here yet"} —
          </div>
        )}
        {items.map((n) => {
          const sev = SEVERITY[n.severity] || SEVERITY.info;
          const SevIcon = sev.icon;
          const isUnread = !n.read_at;
          return (
            <div
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              className={`border ${isUnread ? "border-zinc-700 bg-[#0d0d0d]" : "border-zinc-900 bg-[#0A0A0A] opacity-70"} rounded-sm p-4 flex items-start gap-3`}
            >
              <div className={`shrink-0 mt-0.5 p-1.5 rounded-sm border ${sev.cls} bg-black/40`}>
                <SevIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${sev.cls.split(" ")[0]}`}>{n.severity}</span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5 rounded-sm">
                    {TYPE_LABEL[n.type] || n.type}
                  </span>
                  {n.feed_name && (
                    <NavLink to="/feeds" className="text-[11px] font-mono text-zinc-400 hover:text-white truncate max-w-[240px]" title={n.feed_name}>
                      {n.feed_name}
                    </NavLink>
                  )}
                  <span className="text-[11px] font-mono text-zinc-500 ml-auto" title={n.created_at}>
                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : "—"}
                  </span>
                </div>
                <div className="font-mono text-sm text-zinc-100 truncate" title={n.title}>{n.title}</div>
                {n.message && <div className="mt-1 font-mono text-xs text-zinc-400">{n.message}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {isUnread && (
                  <button
                    data-testid={`mark-read-${n.id}`}
                    onClick={() => markRead.mutate(n.id)}
                    title="Mark as read"
                    className="p-1.5 rounded-sm border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  data-testid={`delete-notification-${n.id}`}
                  onClick={() => delOne.mutate(n.id)}
                  title="Delete"
                  className="p-1.5 rounded-sm border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
